<script lang="ts">
  import { createEventDispatcher } from "svelte";
  const dispatch = createEventDispatcher();

  import type { AnyCell } from "@okcontract/cells";
  import {
    TokenCurrentChain,
    add_prefix_from_search_type,
    isContract,
    isToken,
    link_of_cache_query,
    strip_datacache_prefix
  } from "@okcontract/coredata";
  import {
    DataEditor,
    defaultPatterns,
    type EditorNode
  } from "@okcontract/fred";
  import { parseExpression, prettyPrint } from "@okcontract/lambdascript";
  import type { ChainType } from "@okcontract/multichain";
  import { EnvKeyChain, coredataSearch, type OKPage } from "@okcontract/sdk";
  import { Icon, getTheme, inputStyles } from "@okcontract/uic";
  import { CodeMirror } from "@scv/codemirror";
  import {
    AlertCellError,
    CopyToClipboard,
    DataCacheSnippet,
    Image,
    ResultSnippet
  } from "@scv/dcuic";
  import { SearchSelector } from "@scv/selector";

  import { type EditorOptions, type ModeType } from "../types";
  import {
    canBeImage,
    imageURL,
    initialMode,
    stringValue
  } from "./AdvancedInputEditor.utils";
  import LighthouseUpload from "./LighthouseUpload.svelte";

  export let instance: OKPage;
  const proxy = instance.proxy;

  export let editor: DataEditor;
  const env = editor.env;
  const contractChain = env?.value(EnvKeyChain) as
    | AnyCell<ChainType>
    | undefined;
  export let options: EditorOptions = {};

  export let node: EditorNode;
  export let disabled: boolean;
  const definition = node.definition;
  const value = instance.proxy.get(node.value) as AnyCell<string>;

  // export let stringify = false;
  $: expr = $definition && "isExpr" in $definition && $definition.isExpr;
  const transform = (x: string) => (expr ? JSON.stringify(x) : x);

  const theme = getTheme();
  const compiledTheme = theme.compiled;

  const mode = proxy.new<ModeType>(initialMode(proxy, definition, value));
  let inputRef: HTMLElement;

  // Lighthouse
  // @todo we should not always create this
  const dPath = node.path.join(".");

  // create new LightHouse data when URL is found
  // or update current lighthouse url
  value.subscribe((_value: string | Error) => {
    if (_value instanceof Error) return;
    const url = imageURL(_value);
    instance.core.Lighthouse.addNew(dPath, url);
  });

  const unquoted = proxy.map(
    [value],
    (_v) => stringValue(_v),
    "unquoted",
    true
  );

  // string search selector
  const getSelected = async () => strip_datacache_prefix(await unquoted.get());
  const selected = proxy.new(getSelected(), "selected");
</script>

{#if $value instanceof Error}
  <AlertCellError cell={value} where="AIE" />
{:else if options?.view}
  {#if $mode === "coredata" || isToken($unquoted) || isContract($unquoted)}
    <ResultSnippet
      link={link_of_cache_query($unquoted)}
      size="xs"
      style="default"
    >
      <DataCacheSnippet
        dq={$unquoted}
        small={true}
        displayType={false}
        displayUpvote={false}
      />
    </ResultSnippet>
  {:else if $mode === "expr"}
    <div
      class="font-mono flex flex-wrap break-words overflow-x-auto bg-accent/40 text-accent-content rounded-btn text-sm p-1"
    >
      {#await parseExpression($value)}
        {$value}
      {:then expr}
        <!-- {@html prettyPrintHTML(expr)} -->
        {prettyPrint(expr)}
      {:catch err}
        <span class="bg-yellow/50">
          {$value}
        </span>
      {/await}
    </div>
    <!-- @todo restore -->
    <!--   <CodeMirror
      value={$value}
      readonly={true}
      editable={false}
    />-->
  {:else if $mode === "img"}
    <Image src={$unquoted} style="sm" />
    <span class="font-medium break-all">{$unquoted}</span>
  {:else if $mode === "string"}
    <span class="font-medium">{$unquoted}</span>
  {/if}
{:else}
  {@const search =
    $definition && "search" in $definition ? $definition.search : null}
  {@const string = !$value || defaultPatterns.string.test($value)}
  {@const image =
    "isImg" in $definition && $definition.isImg && canBeImage($value)}
  {@const asString = (string && !search) || image}
  {@const count =
    (expr ? 1 : 0) + (asString ? 1 : 0) + (image ? 1 : 0) + (search ? 1 : 0)}
  {#if count > 1 && ($mode === "string" || $mode === "img" || $mode === "expr" || $mode === "coredata")}
    <div
      role="tablist"
      class="tabs tabs-lifted justify-self-start -mb-[var(--tab-border)]"
    >
      {#if expr}
        <button
          type="button"
          class="tab {$mode === 'expr' ? 'tab-active' : ''}"
          on:click={() => ($mode = "expr")}
        >
          <Icon name="formula" />
        </button>
      {/if}
      {#if asString}
        <button
          type="button"
          class="tab {$mode === 'string' ? 'tab-active' : ''}"
          on:click={() => ($mode = "string")}
        >
          <Icon name="string" />
        </button>
      {/if}
      {#if image}
        <button
          type="button"
          class="tab {$mode === 'img' ? 'tab-active' : ''}"
          on:click={() => ($mode = "img")}
        >
          <Icon name="image" />
        </button>
      {/if}
      {#if search}
        <button
          type="button"
          class="tab {$mode === 'coredata' ? 'tab-active' : ''}"
          on:click={() => ($mode = "coredata")}
        >
          <Icon name="search" />
        </button>
      {/if}
      <div class="tab" />
    </div>
  {/if}
  <!-- <div
    class="border {count > 1
      ? 'rounded-b-btn rounded-se-btn'
      : 'rounded-btn'} {theme.dark(
      $compiledTheme,
      'input-white-alpha',
      'input-black-alpha',
      'bg-base-100 border-base-300'
    )}"
  > -->
  {#if $mode === "string"}
    {@const placeholder = "pl" in $definition ? $definition.pl : undefined}
    <div
      class="border {count > 1
        ? 'rounded-b-btn rounded-se-btn'
        : 'rounded-btn'} {theme.dark(
        $compiledTheme,
        'input-white-alpha',
        'input-black-alpha',
        'bg-base-100 border-base-300'
      )}"
    >
      <label class="form-control w-full">
        <input
          bind:this={inputRef}
          class={inputStyles["bordered-ghost"]}
          {disabled}
          value={$unquoted}
          {placeholder}
          on:input={(event) =>
            dispatch("update", transform(event.currentTarget.value))}
        />
      </label>
    </div>
  {:else if $mode === "expr"}
    {#key inputRef}
      <div
        class="border {count > 1
          ? 'rounded-b-btn rounded-se-btn'
          : 'rounded-btn'} {theme.dark(
          $compiledTheme,
          'input-white-alpha',
          'input-black-alpha',
          'bg-base-100 border-base-300'
        )}"
      >
        <div class="grid p-2">
          <div class="w-full col-start-1 row-start-1">
            <!-- @todo migrate to Cell -->
            <!-- @todo env -->
            <CodeMirror
              on:change={(event) => dispatch("update", event.detail)}
              value={$value.toString()}
              readonly={disabled}
              editable={!disabled}
              {env}
            />
          </div>
          {#if expr}
            <div
              class="col-start-1 row-start-1 flex items-start justify-end p-2 rtl:justify-start"
            >
              <!-- @todo migrate to Cell -->
              <CopyToClipboard content={$value} pos="left" />
            </div>
          {/if}
        </div>
      </div>
    {/key}
  {:else if $mode === "img"}
    <div
      class="p-2 border {count > 1
        ? 'rounded-b-btn rounded-se-btn'
        : 'rounded-btn'} {theme.dark(
        $compiledTheme,
        'input-white-alpha',
        'input-black-alpha',
        'bg-base-100 border-base-300'
      )}"
    >
      <LighthouseUpload
        {instance}
        key={dPath}
        {disabled}
        on:update={(ev) => dispatch("update", transform(ev.detail))}
      />
    </div>
  {:else if $mode === "coredata" && search}
    {@const placeholder = `Search ${search === TokenCurrentChain ? `token on ${$contractChain}` : search}...`}
    {@const searchFn = coredataSearch(instance, search, contractChain)}
    <SearchSelector
      {instance}
      {selected}
      {searchFn}
      {placeholder}
      inputStyle={count > 1 ? "default" : "bordered"}
      {disabled}
      delay={1000}
      on:select={(ev) => {
        const selected = ev.detail.selected;
        const component = ev.detail.component;
        component.close();
        component.selected.set(selected);

        const prefixedValue = value
          ? add_prefix_from_search_type(search, selected)
          : "";
        dispatch("update", transform(prefixedValue));
      }}
      let:value
      borderStyle={count > 1 ? "rounded-b-btn rounded-se-btn" : "rounded-btn"}
    >
      <DataCacheSnippet
        dq={add_prefix_from_search_type(search, value)}
        small={true}
      />
    </SearchSelector>
  {/if}
  <!-- </div> -->
{/if}
